import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useContext, useLayoutEffect, useCallback } from 'react';
import { ButtonDownIcon, ButtonUpIcon } from '@manulife/mux-cds-icons';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import { CONTENT_TYPE } from './constants.js';
import SideNavContext from './SideNavContext.js';
import { SubItem, SubItemComponent, SubNavDropdownList } from './styledComponents.js';
import { hasActiveChild, getNavItemProps } from './utils.js';
import useTheme from '../../hooks/useTheme.js';
import useMedia from '../../hooks/useMedia.js';
import useToggle from '../../hooks/useToggle.js';

var _excluded = ["id", "as", "label", "itemKey", "contentType", "to", "onClick", "children"];
var SubNavItem = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      as = _ref.as,
      label = _ref.label,
      itemKey = _ref.itemKey,
      contentType = _ref.contentType,
      to = _ref.to,
      onClick = _ref.onClick,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var safeProps = safeRest(rest);

  var _useContext = useContext(SideNavContext),
      keepOpenOnSelect = _useContext.keepOpenOnSelect,
      activeKey = _useContext.activeKey,
      expandedParentKey = _useContext.expandedParentKey,
      handleNavItemClick = _useContext.handleNavItemClick,
      setActiveParentKey = _useContext.setActiveParentKey,
      setExpandedParentKey = _useContext.setExpandedParentKey;

  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")], // media queries
  [true], // values corresponding to media query
  false // default value
  );

  var _useToggle = useToggle(false),
      _useToggle2 = _slicedToArray(_useToggle, 3),
      isDropdownExpanded = _useToggle2[0],
      toggleDropdown = _useToggle2[1],
      setDropdownExpanded = _useToggle2[2];

  var isActive = activeKey === itemKey; // useLayoutEffect so the effect is run before the browser has finished layout and paint
  // with useEffect there is a delay when one dropdown is closed and another is opened

  useLayoutEffect(function () {
    var isActiveDropdown = contentType === CONTENT_TYPE.DROPDOWN && (hasActiveChild(children, activeKey) || isActive);
    setDropdownExpanded(isActiveDropdown);
  }, [isActive, activeKey, contentType, children, setDropdownExpanded]);
  var component = showHamburgerMenu && contentType === CONTENT_TYPE.DROPDOWN ? 'button' : as;
  var subNavItemProps = getNavItemProps(component, to, contentType === CONTENT_TYPE.DROPDOWN, isDropdownExpanded);
  var DropdownIcon = isActive || isDropdownExpanded ? ButtonDownIcon : ButtonUpIcon; // NOSONAR

  var handleClick = useCallback(function (e) {
    if (contentType === CONTENT_TYPE.DROPDOWN) {
      toggleDropdown();
    }

    if (!showHamburgerMenu || showHamburgerMenu && contentType !== CONTENT_TYPE.DROPDOWN) {
      handleNavItemClick(itemKey);
    }

    if (!keepOpenOnSelect && contentType !== CONTENT_TYPE.DROPDOWN) {
      setExpandedParentKey();
    }

    setActiveParentKey(expandedParentKey);
    handle(onClick)(e);
  }, [showHamburgerMenu, handleNavItemClick, setActiveParentKey, setExpandedParentKey, keepOpenOnSelect, itemKey, expandedParentKey, onClick, contentType, toggleDropdown]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SubItem, {
    role: "none",
    theme: theme
  }, /*#__PURE__*/React.createElement(SubItemComponent, _extends({}, subNavItemProps, safeProps, {
    id: id,
    as: component,
    role: "menuitem",
    "aria-current": isActive ? 'page' : false,
    "data-selected": isActive,
    "data-testid": "sub-item-".concat(id),
    onClick: handleClick,
    theme: theme,
    ref: ref,
    $contentType: contentType,
    $isActive: isActive
  }), label, contentType === CONTENT_TYPE.DROPDOWN ? /*#__PURE__*/React.createElement(DropdownIcon, {
    fontSize: rem(SVG_ICON_SIZE_DEFAULT),
    style: {
      position: 'absolute',
      right: '23px'
    }
  }) : children)), contentType === CONTENT_TYPE.DROPDOWN && (isActive || isDropdownExpanded) && /*#__PURE__*/React.createElement(SubNavDropdownList, {
    theme: theme
  }, children));
});
process.env.NODE_ENV !== "production" ? SubNavItem.propTypes = {
  /** id for SubNavItem */
  id: PropTypes.string,

  /** type of component to use as nav item (e.g. Link from react-router-dom, button, etc.) */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /** label for SubNavItem */
  label: PropTypes.node,

  /** unique key used for item selection */
  itemKey: PropTypes.string.isRequired,

  /** type of content displayed in the sub nav */
  contentType: PropTypes.oneOf([CONTENT_TYPE.BANNER, CONTENT_TYPE.DROPDOWN]),

  /** url (by default maps to href) or url/object/function (when using Link from react-router-dom) */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func]),

  /** onClick handler for item */
  onClick: PropTypes.func,

  /** content inside banner when `contentType={CONTENT_TYPE.BANNER}` */
  children: PropTypes.node
} : void 0;
SubNavItem.defaultProps = {
  id: undefined,
  as: 'a',
  label: undefined,
  contentType: undefined,
  to: undefined,
  onClick: noop,
  children: undefined
};
process.env.NODE_ENV !== "production" ? SubNavItem.displayName = 'SubNavItem' : void 0;
var SubNavItem$1 = SubNavItem;

export { SubNavItem$1 as default };
